import { BARE_METAL_NODES_OPTION_META_KEYS } from '../../../capabilities/bare-metal-nodes/fieldKeys';
import { formatBareMetalMemoryText } from '../../../capabilities/bare-metal-nodes/utils/billingNodeMeta';
import { BASE_INFO_OPTION_META_KEYS } from '../../../capabilities/base-info/fieldKeys';
import { NETWORK_OPTION_META_KEYS } from '../../../capabilities/network/fieldKeys';
import { serializeServiceListForApi } from '../../../capabilities/service/serviceApiField';
import type { PipelineContext } from '../../../core/types';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { resolveOptionDisplayValue } from '../../../platform/option-meta';
import type { ResourceType } from '../../../platform/pipeline-kernel/types';
import { stripEmptyObjects } from '../../../utils';
import { buildResourceConfigFromPricing } from '../../../utils/pricingResourceMeta';
import { bareMetalNodeResource } from '../resourceConfigs';
import { filterBareMetalSpecByEnabledModules, isBareMetalModuleEnabled } from './enabledModules';
import type { BareMetalApiPayload, BareMetalSpec } from './schema';

const PREVIEW_EMPTY = undefined;

function previewValue(value: unknown): string | undefined {
  if (value === undefined || value === null) return PREVIEW_EMPTY;
  if (typeof value === 'string' && !value.trim()) return PREVIEW_EMPTY;
  return String(value);
}

function previewRow(id: string, group: string, label: string, value: unknown): DisplayDescriptor {
  return { id, group, label, value: previewValue(value) ?? '' };
}

function filterConfirmDescriptors(items: DisplayDescriptor[]): DisplayDescriptor[] {
  const result: DisplayDescriptor[] = [];
  for (const item of items) {
    const children = item.children?.length ? filterConfirmDescriptors(item.children) : undefined;
    const value = String(item.value ?? '').trim();
    if (!children?.length && (!value || value === PREVIEW_EMPTY)) continue;
    result.push(children?.length ? { ...item, children } : item);
  }
  return result;
}

export function serializeBareMetalApiBody(spec: BareMetalSpec, ctx: PipelineContext): BareMetalApiPayload {
  const creator = ctx.overlay?.creator ?? '';

  const provisioningMode = spec.provisioning?.mode === 'Adopt' ? 'Adopt' : 'Install';
  const payload: BareMetalApiPayload = {
    nickname: spec.name,
    description: spec.description,
    creator,
    bmsNode: spec.bmsNode,
    powerPolicy: spec.powerPolicy,
    provisioning: { mode: provisioningMode },
    boot: { mode: spec.boot?.mode || 'PXE', ...(spec.boot?.revision ? { revision: spec.boot.revision } : {}) },
    network: {
      vpc: spec.network.vpc,
      subnet: spec.network.subnet,
      securitygroup: spec.network.securitygroup,
      ...(provisioningMode === 'Adopt' && spec.network.staticIpv4 ? { staticIpv4: spec.network.staticIpv4 } : {}),
      services: serializeServiceListForApi(spec.network.services),
    },
    systemVolume: spec.systemVolume,
    ...(spec.account ? { account: spec.account } : {}),
    ...(spec.system?.options ? { system: spec.system } : {}),
  };

  if (spec.workspace) {
    payload.workspaces = spec.workspace;
  }

  return stripEmptyObjects(payload);
}

export function pickBareMetalPresetFields(spec: BareMetalSpec, ctx: PipelineContext): unknown {
  const enabledModules = ctx.sku?.enabledModules;
  const skuType = ctx.sku?.skuType;
  const filtered = filterBareMetalSpecByEnabledModules(spec, enabledModules, skuType);
  return {
    name: filtered.name,
    description: filtered.description,
    workspace: filtered.workspace,
    bmsNode: filtered.bmsNode,
    powerPolicy: filtered.powerPolicy,
    provisioning: filtered.provisioning,
    boot: filtered.boot,
    network: filtered.network,
    systemVolume: filtered.systemVolume,
    /** 密码/公钥不进 SKU preset，下单或直建时由用户填写 */
    ...(filtered.optionMeta ? { optionMeta: filtered.optionMeta } : {}),
    ...(enabledModules ? { skuResourceModules: enabledModules } : {}),
  };
}

export interface BuildBareMetalDisplayOptions {
  displayMode?: 'productCard' | 'confirm';
}

export function buildBareMetalDisplayDescriptors(
  spec: BareMetalSpec,
  ctx: PipelineContext,
  options: BuildBareMetalDisplayOptions = {},
): DisplayDescriptor[] {
  const enabledModules = ctx.sku?.enabledModules;
  const skuType = ctx.sku?.skuType;
  const filtered = filterBareMetalSpecByEnabledModules(spec, enabledModules, skuType) as BareMetalSpec;
  const optionMetaForm = spec.optionMeta ? { optionMeta: spec.optionMeta } : {};
  const network = filtered.network;

  const baseInfo: DisplayDescriptor[] = isBareMetalModuleEnabled('baseInfo', enabledModules, skuType)
    ? [
        previewRow('name', 'baseInfo', '名称', filtered.name),
        previewRow('description', 'baseInfo', '描述', filtered.description),
        previewRow(
          'workspace',
          'baseInfo',
          '租户空间',
          resolveOptionDisplayValue(optionMetaForm, 'workspace', filtered.workspace, BASE_INFO_OPTION_META_KEYS.workspace),
        ),
        previewRow('namespace', 'baseInfo', '命名空间', ctx.namespace),
        previewRow('datacenter', 'baseInfo', '数据中心', filtered.datacenter ?? ctx.cluster),
      ]
    : [];

  const vpc = resolveOptionDisplayValue(optionMetaForm, 'vpc.name', network?.vpc?.name, NETWORK_OPTION_META_KEYS.vpc);
  const provisioningMode = filtered.provisioning?.mode === 'Adopt' ? 'Adopt' : 'Install';
  const networkItems: DisplayDescriptor[] = isBareMetalModuleEnabled('network', enabledModules, skuType)
    ? [
        previewRow('vpc', 'network', 'VPC', vpc),
        previewRow(
          'subnet',
          'network',
          '子网',
          resolveOptionDisplayValue(optionMetaForm, 'subnet.name', network?.subnet?.name, NETWORK_OPTION_META_KEYS.subnet),
        ),
        previewRow(
          'securitygroup',
          'network',
          '安全组',
          resolveOptionDisplayValue(
            optionMetaForm,
            'securitygroup.name',
            network?.securitygroup?.name,
            NETWORK_OPTION_META_KEYS.securitygroup,
          ),
        ),
        ...(provisioningMode === 'Adopt' ? [previewRow('staticIpv4', 'network', '静态 IPv4', network?.staticIpv4)] : []),
      ]
    : [];

  const imageMeta = optionMetaForm.optionMeta?.image as Record<string, unknown> | undefined;
  const systemSetting: DisplayDescriptor[] = isBareMetalModuleEnabled('systemSetting', enabledModules, skuType)
    ? [
        previewRow('provisioningMode', 'systemSetting', '系统交付方式', provisioningMode === 'Adopt' ? '纳管已有系统' : '安装新系统'),
        ...(provisioningMode === 'Install'
          ? [
              previewRow('bootMode', 'systemSetting', '引导方式', filtered.boot?.mode || 'PXE'),
              previewRow(
                'systemImage',
                'systemSetting',
                '系统镜像',
                (imageMeta?.name as string | undefined) || (imageMeta?.label as string | undefined) || filtered.systemVolume?.image,
              ),
            ]
          : []),
        previewRow('powerPolicy', 'systemSetting', '电源策略', filtered.powerPolicy),
        previewRow('systemOs', 'systemSetting', '操作系统', filtered.systemVolume?.os),
        previewRow('systemVersion', 'systemSetting', '版本', filtered.systemVolume?.version),
        previewRow('systemArchitecture', 'systemSetting', '架构', filtered.systemVolume?.architecture),
      ]
    : [];

  const bareMetalNodes: DisplayDescriptor[] = isBareMetalModuleEnabled('bareMetalNodes', enabledModules, skuType)
    ? (() => {
        const nodeMeta = optionMetaForm.optionMeta?.[BARE_METAL_NODES_OPTION_META_KEYS.node] as Record<string, unknown> | undefined;
        const nodeValue = filtered.bmsNode || (nodeMeta?.name as string | undefined) || (nodeMeta?.label as string | undefined);
        if (nodeMeta) {
          return [
            previewRow('bmsNode', 'bareMetalNodes', '节点', nodeValue),
            previewRow('bmsArch', 'bareMetalNodes', '架构', (nodeMeta as any)?.spec?.architecture),
            previewRow(
              'bmsCpu',
              'bareMetalNodes',
              'CPU',
              (nodeMeta as any)?.spec?.cpu?.totalCores ? `${(nodeMeta as any).spec.cpu.totalCores}核` : undefined,
            ),
            previewRow(
              'bmsMemory',
              'bareMetalNodes',
              '内存',
              (nodeMeta as any)?.spec?.memory?.totalMemory
                ? formatBareMetalMemoryText(
                    (nodeMeta as any).spec.memory.totalMemory,
                    (nodeMeta as any).spec.memory.unit,
                    (nodeMeta as any).spec.memory.supportedUnits,
                  )
                : undefined,
            ),
            previewRow(
              'bmsGpu',
              'bareMetalNodes',
              '显卡',
              (nodeMeta as any)?.spec?.gpus ? (nodeMeta as any).spec.gpus.map((g: any) => `${g.model}*${g.num}`).join(', ') : undefined,
            ),
            previewRow('bmsManufacturer', 'bareMetalNodes', '厂商', (nodeMeta as any)?.spec?.manufacturer),
            previewRow('bmsModel', 'bareMetalNodes', '型号', (nodeMeta as any)?.spec?.model),
          ];
        }
        return [previewRow('bmsNode', 'bareMetalNodes', '节点', nodeValue)];
      })()
    : [];

  const descriptors = [...baseInfo, ...systemSetting, ...networkItems, ...bareMetalNodes];
  return options.displayMode === 'confirm' ? filterConfirmDescriptors(descriptors) : descriptors;
}

export function buildBareMetalResourceConfigs(spec: BareMetalSpec, ctx: PipelineContext): ResourceType[] {
  const filtered = filterBareMetalSpecByEnabledModules(spec, ctx.sku?.enabledModules, ctx.sku?.skuType);
  const nodeMeta = filtered.optionMeta?.[BARE_METAL_NODES_OPTION_META_KEYS.node] as Record<string, unknown> | undefined;

  if (nodeMeta?.billItemId || nodeMeta?.vendorID || nodeMeta?.deviceID) {
    return [
      buildResourceConfigFromPricing(
        bareMetalNodeResource,
        {
          vendor: nodeMeta?.vendor as string | undefined,
          device: nodeMeta?.device as string | undefined,
          vendorID: nodeMeta?.vendorID as string | undefined,
          deviceID: nodeMeta?.deviceID as string | undefined,
          quantityUnit: nodeMeta?.quantityUnit != null ? String(nodeMeta.quantityUnit) : undefined,
        },
        bareMetalNodeResource.requirement || '1',
      ),
    ];
  }

  if (filtered.bmsNode) {
    return [{ ...bareMetalNodeResource }];
  }

  return [];
}
